import React, { FC, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  Alert,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import { AddNoteIcon } from '../../../../vectors/index';
import { useCreateApplicationNote } from '@features/applicationNotes/hooks/useCreateApplicationNote';
import { useGetSavedMessages } from '@features/savedMessages/hooks/useGetSavedMessages';
import { toast } from 'react-toastify';

type CreateNoteModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  applicationId: number;
};

const CreateNoteModal: FC<CreateNoteModalProps> = ({
  isOpen,
  handleClose,
  applicationId,
}) => {
  const [errorText, setErrorText] = useState('');
  const [name, setName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [option, setOption] = useState('template');

  const { createNote, isCreateNoteLoading } = useCreateApplicationNote({
    setErrorText,
  });
  const { savedMessages } = useGetSavedMessages({ enabled: true, isNote: true });

  const handleChangeTemplate = (event: { target: { value: string }; }) => {
    const selectedMessage = savedMessages?.object?.find(
      (msg) => msg.id.toString() === event.target.value
    );
    setSelectedTemplate(event.target.value);
  };

  const handleCreateNew = () => {
    if (option === 'template' && selectedTemplate) {
      const selectedMessage = savedMessages?.object?.find(
        (msg) => msg.id.toString() === selectedTemplate
      );
      if (selectedMessage) {
        createNote(
          {
            application_id: applicationId,
            name: selectedMessage.label,
            note: selectedMessage.content,
          },
          {
            onSuccess: () => {
              toast.success('Note added successfully')
              setSelectedTemplate('');
              handleClose();
            }
          }
        );
      }
    } else {
      createNote(
        { application_id: applicationId, note: '', name },
        {
          onSuccess: () => {
            toast.success('Note added successfully')
            handleClose();
            setName('');
          },
        }
      );
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <AddNoteIcon />
        <br />
        <Typography variant="h4">Create a Note</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ marginBottom: 4 }} spacing={2}>
          {errorText && <Alert severity="error">{errorText}</Alert>}

          <RadioGroup
            value={option}
            onChange={(e) => setOption(e.target.value)}
            row
          >
            <FormControlLabel
              value="template"
              control={<Radio />}
              label="Create from an existing template"
            />
            <FormControlLabel
              value="new"
              control={<Radio />}
              label="Create new"
            />
          </RadioGroup>

          {option === 'template' ? (
            <Select
              value={selectedTemplate}
              onChange={handleChangeTemplate}
              displayEmpty
              variant="outlined"
              size="small"
              fullWidth
              MenuProps={{
                disablePortal: true,
              }}
            >
              <MenuItem value="" disabled>
                Select template
              </MenuItem>
              {savedMessages?.object?.map((message) => (
                <MenuItem key={message.id} value={message.id.toString()}>
                  {message.label}
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TextField
              value={name}
              size="small"
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              inputProps={{ maxLength: 100 }}
              fullWidth
            />
          )}

          <LoadingButton
            variant="contained"
            onClick={handleCreateNew}
            disabled={option === 'new' && !name}
            loading={isCreateNoteLoading}
            sx={{ fontSize: '16px', marginTop: '10px !imporant' }}
          >
            Create Note
          </LoadingButton>

          <Button
            sx={{ marginTop: '10px !imporant' }}
            variant="outlined"
            color="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CreateNoteModal;
